const BASE = '/api/v1';

// Raised when the server says "sign in first", so the whole app can show the login page.
export const UNAUTHORIZED_EVENT = 'menagtm:unauthorized';

async function request(path, options) {
  const res = await fetch(`${BASE}${path}`, { credentials: 'same-origin', ...options });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail || detail;
    } catch {
      // response had no JSON body
    }
    // A wrong password on the login form is also a 401, but that is not a lost session.
    if (res.status === 401 && !path.startsWith('/auth/login')) {
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }
    throw new Error(`${res.status} ${detail}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

function toQuery(params) {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => usp.append(key, item));
      return;
    }
    if (value !== undefined && value !== null && value !== '') {
      usp.set(key, value);
    }
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function getStats(params) {
  return request(`/stats${toQuery(params)}`);
}

export function getSignals(params) {
  return request(`/signals${toQuery(params)}`);
}

export function getAccountSignals(accountId, params) {
  return request(`/accounts/${accountId}/signals${toQuery(params)}`);
}

export function listContacts(accountIds) {
  return request(`/contacts${toQuery({ account_ids: accountIds })}`);
}

export function researchTopContacts(accountIds) {
  return request('/contacts/research', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ account_ids: accountIds, limit_per_account: 3 }),
  });
}

export function reviewContact(contactId, researchStatus) {
  return request(`/contacts/${contactId}/review`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ research_status: researchStatus }),
  });
}

export function createContact(accountId, payload) {
  return request(`/accounts/${accountId}/contacts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function deleteContact(contactId) {
  return request(`/contacts/${contactId}`, { method: 'DELETE' });
}

export function getScoringConfig() {
  return request('/scoring-config');
}

export function previewAccountScores(
  config,
  { sources, accountIds, includeSignalBreakdown = false } = {}
) {
  return request('/scoring/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sources,
      account_ids: accountIds,
      include_signal_breakdown: includeSignalBreakdown,
      weights: config.weights,
      half_life_days: config.halfLifeDays,
      corroboration_min_families: config.corroborationMinFamilies,
      corroboration_bonus: config.corroborationBonus,
      corroboration_window_days: config.corroborationWindowDays,
      tier_a_min: config.tierAMin,
      tier_b_min: config.tierBMin,
      saturation_k: config.saturationK,
    }),
  });
}

export function getIngestionConfig() {
  return request('/ingestion/config');
}

export function triggerIngestionRun(options) {
  return request('/ingestion/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options || {}),
  });
}

export function getIngestionStatus() {
  return request('/ingestion/status');
}

export function listTargetAccounts(params) {
  return request(`/target-accounts${toQuery(params)}`);
}

export function createTargetAccount(payload) {
  return request('/target-accounts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function updateTargetAccount(id, payload) {
  return request(`/target-accounts/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function deleteTargetAccount(id) {
  return request(`/target-accounts/${id}`, { method: 'DELETE' });
}

export function resolveTargetAccount(id) {
  return request(`/target-accounts/${id}/resolve`, { method: 'POST' });
}

export function runAllTargetAccounts(params) {
  return request(`/target-accounts/run-all${toQuery(params)}`, { method: 'POST' });
}

export function runTargetAccount(id, params) {
  return request(`/target-accounts/${id}/run${toQuery(params)}`, { method: 'POST' });
}

export function listAccountMergeCandidates(params) {
  return request(`/account-merges${toQuery(params)}`);
}

export function scanAccountMergeCandidates() {
  return request('/account-merges/scan', { method: 'POST' });
}

export function approveAccountMergeCandidate(id) {
  return request(`/account-merges/${id}/approve`, { method: 'POST' });
}

export function rejectAccountMergeCandidate(id) {
  return request(`/account-merges/${id}/reject`, { method: 'POST' });
}

const MAX_PAGES = 25; // 25 * 200 = 5,000 signals — generous ceiling for a client-side scoring pass

/** Page through /signals until exhausted (or MAX_PAGES hit) and return every item. */
export async function getAllSignals(params) {
  const all = [];
  let offset = 0;
  const limit = 200;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data = await getSignals({ ...params, limit, offset });
    all.push(...data.items);
    if (!data.next_cursor || data.items.length === 0) break;
    offset += limit;
  }
  return all;
}

// --- Company Corpus: data-supplier candidates (kept apart from AI-buyer results) ---
export function listCorpusCompanies() {
  return request('/corpus/companies');
}

export function importCorpusCompanies(rows) {
  return request('/corpus/companies/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
}

export function addCorpusCompany(domain) {
  return request('/corpus/companies', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain }),
  });
}

export function approveCorpusCompanies(accountIds) {
  return request('/corpus/companies/approve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ account_ids: accountIds }),
  });
}

export function findPeopleForCompanies(accountIds) {
  return request('/corpus/companies/find-people', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ account_ids: accountIds }),
  });
}

export function reviewCorpusCompany(accountId, decision, reason) {
  return request(`/corpus/companies/${accountId}/review`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decision, reason: reason || null }),
  });
}

export function getHunterCredits() {
  return request('/corpus/hunter-credits');
}

export function refreshCorpusCoverage(options) {
  return request('/corpus/coverage', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh: false, ...(options || {}) }),
  });
}

export function listCorpusContacts(accountId) {
  return request(`/corpus/companies/${accountId}/contacts`);
}

export function findCorpusContacts(accountId) {
  return request(`/corpus/companies/${accountId}/contacts`, { method: 'POST' });
}

export function verifyCorpusRoles(accountId) {
  return request(`/corpus/companies/${accountId}/verify-roles`, { method: 'POST' });
}

export function confirmCorpusRole(accountId, contactId, url, note, title) {
  return request(`/corpus/companies/${accountId}/contacts/${contactId}/role`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, note, ...(title ? { title } : {}) }),
  });
}

export function setCorpusPriority(accountId, contactId, priority) {
  return request(`/corpus/companies/${accountId}/contacts/${contactId}/priority`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ priority }),
  });
}

// --- Sign in ---
export function getMe() {
  return request('/auth/me');
}

export function login(email, password) {
  return request('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
}

export function logout() {
  return request('/auth/logout', { method: 'POST' });
}

export function getCorpusHistory(accountId) {
  return request(`/corpus/companies/${accountId}/history`);
}


// --- Company Corpus: MenaOS invitation links and registration updates ---
export function listCorpusInvitations(accountId) {
  return request(`/corpus/companies/${accountId}/invitations`);
}

export function createCorpusInvitation(accountId, contactId) {
  return request(`/corpus/companies/${accountId}/contacts/${contactId}/invitation`, { method: 'POST' });
}

export function markCorpusInvitationSent(invitationId) {
  return request(`/corpus/invitations/${invitationId}/sent`, { method: 'POST' });
}

export function createAllCorpusInvitations(accountId) {
  return request(`/corpus/companies/${accountId}/invitations/create-all`, { method: 'POST' });
}

export function markSendListSent() {
  return request('/corpus/send-list/mark-sent', { method: 'POST' });
}

// --- invitation emails, sent through SendGrid by the server ---
export function getEmailSettings() {
  return request('/corpus/email-settings');
}

export function previewInvitationEmail(invitationId) {
  return request(`/corpus/invitations/${invitationId}/email`);
}

export function sendInvitationEmail(invitationId) {
  return request(`/corpus/invitations/${invitationId}/send`, { method: 'POST' });
}

export function getEmailTemplate() {
  return request('/corpus/email-template');
}

export function saveEmailTemplate(subject, body) {
  return request('/corpus/email-template', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, body }),
  });
}

// recipient: an invitation id (string, for older callers) or { invitation_id, contact_id }.
export function previewEmailTemplate(subject, body, recipient) {
  const who = typeof recipient === 'string' ? { invitation_id: recipient } : recipient || {};
  return request('/corpus/email-template/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, body, invitation_id: who.invitation_id || null, contact_id: who.contact_id || null }),
  });
}

// Who "Send email" would reach at these companies, before anything is made or sent.
export function previewSendToCompanies(accountIds) {
  return request('/corpus/companies/send-preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ account_ids: accountIds }),
  });
}

export function sendInvitationsToCompanies(accountIds) {
  return request('/corpus/companies/send-invitations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ account_ids: accountIds }),
  });
}

export function revokeCorpusInvitation(invitationId, reason) {
  return request(`/corpus/invitations/${invitationId}/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export function getMenaosSync() {
  return request('/corpus/menaos-sync');
}

export function runMenaosSync() {
  return request('/corpus/menaos-sync/run', { method: 'POST' });
}

export function restartMenaosSync() {
  return request('/corpus/menaos-sync/restart', { method: 'POST' });
}

export function listMenaosUnmatched() {
  return request('/corpus/menaos-sync/unmatched');
}

export function attachMenaosRegistration(reference, invitationId, note) {
  return request(`/corpus/menaos-sync/registrations/${encodeURIComponent(reference)}/attach`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ invitation_id: invitationId, note }),
  });
}

export function dismissMenaosRegistration(reference, note) {
  return request(`/corpus/menaos-sync/registrations/${encodeURIComponent(reference)}/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note }),
  });
}

export function reviewMatchedRegistration(reference, decision, note) {
  return request(`/corpus/menaos-sync/registrations/${encodeURIComponent(reference)}/review`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decision, note }),
  });
}

export function stopCompanyInvitations(accountId, reason) {
  return request(`/corpus/companies/${accountId}/stop-invitations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export function resumeCompanyInvitations(accountId, reason) {
  return request(`/corpus/companies/${accountId}/resume-invitations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export function listAllCorpusInvitations() {
  return request('/corpus/invitations');
}
